// src/pages/SpecializedElectricalInstallations.jsx
import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import "../style.css";

const pub = process.env.PUBLIC_URL;
// Build a URL to /public assets. Do NOT include "/public" in the filename.
const fromPublic = (filename) => `${pub}/${filename}`;

export default function SpecializedElectricalInstallations() {
  useEffect(() => {
    if (window.AOS && window.AOS.refresh) window.AOS.refresh();
  }, []);

  // Make sure these files exist at: public/speaclizedinstallone.jpg and public/speaclizedinstalltwo.jpg
  const imgBay = fromPublic("speaclizedinstallone.jpg");
  const imgMcc = fromPublic("speaclizedinstalltwo.jpg");

  return (
    <>
      {/* ────────────────── PAGE HEADER / BREADCRUMB ────────────────── */}
      <section
        className="page-header-breadcrumb"
        data-aos="fade-up"
        style={{ backgroundColor: "var(--bg-light)", padding: "2rem 0" }}
      >
        <h1 className="section-title" style={{ marginBottom: "0.5rem" }}>
          Specialized Electrical Installations
        </h1>
        <p className="page-breadcrumb">
          <Link to="/">Home</Link> / <Link to="/services">Services</Link> /{" "}
          <span>Specialized Electrical Installations</span>
        </p>
      </section>

      {/* ───────────────────────── INTRO ────────────────────────────── */}
      <section className="service-intro" data-aos="fade-up">
        <div className="service-intro-content">
          <div
            className="service-intro-image"
            data-aos="fade-right"
            data-aos-delay="100"
          >
            <img
              src={imgBay}
              alt="LPG filling plant – hazardous area electrical installation"
            />
          </div>

          <div
            className="service-intro-text"
            data-aos="fade-left"
            data-aos-delay="200"
          >
            <h2>Hazardous-Area & Industrial Installations</h2>
            <p>
              Mosike Electrical Contractors delivers turnkey installations for
              high-risk and process environments—LPG filling plants, fuel
              depots, manufacturing lines and utilities. We engineer, install
              and test systems that meet the strictest safety and performance
              standards.
            </p>
            <ul className="service-features">
              <li>
                <i className="fas fa-check-circle" /> EX / flameproof equipment
                (ATEX, IECEx, SANS&nbsp;10108)
              </li>
              <li>
                <i className="fas fa-check-circle" /> Motor Control Centres
                (MCCs) &amp; control panels
              </li>
              <li>
                <i className="fas fa-check-circle" /> Variable speed drives,
                soft starters &amp; PLC I/O
              </li>
              <li>
                <i className="fas fa-check-circle" /> Instrumentation, weighing
                &amp; process interlocks
              </li>
              <li>
                <i className="fas fa-check-circle" /> Gas detection, emergency
                stop &amp; alarm systems
              </li>
              <li>
                <i className="fas fa-check-circle" /> Earthing, bonding &amp;
                hazardous-area cable management
              </li>
            </ul>
            <Link to="/contact" className="btn">
              Request Site Assessment
            </Link>
          </div>
        </div>
      </section>

      {/* ──────────────────────── CAPABILITIES ──────────────────────── */}
      <section className="service-benefits" data-aos="fade-up">
        <h2 className="section-title">What We Build &amp; Integrate</h2>
        <p className="section-description">
          Integrated power, control and safety for hazardous &amp;
          mission-critical plants.
        </p>

        <div className="benefits-grid">
          {[
            {
              icon: "fas fa-shield-alt",
              title: "Hazardous-Area Compliance",
              text:
                "Design & selection per SANS 10108 with documentation: zoning, apparatus selection and marking.",
            },
            {
              icon: "fas fa-sliders-h",
              title: "Drives & Motor Control",
              text:
                "VFDs/soft starters, overload protection, interlocks and safe-torque-off for pumps & conveyors.",
            },
            {
              icon: "fas fa-microchip",
              title: "PLC/SCADA Integration",
              text:
                "Clean panel wiring, field marshalling, remote I/O, and tested logic with FAT/SAT sign-off.",
            },
            {
              icon: "fas fa-project-diagram",
              title: "Process Safety Functions",
              text:
                "E-Stops, gas detection, leak/level sensors, shutdown sequences and alarms with proof testing.",
            },
          ].map((b, i) => (
            <div
              key={i}
              className="benefit-card"
              data-aos="fade-up"
              data-aos-delay={100 * (i + 1)}
            >
              <div className="benefit-icon">
                <i className={b.icon} />
              </div>
              <div className="benefit-content">
                <h3>{b.title}</h3>
                <p>{b.text}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ───────────────────────── GALLERY ──────────────────────────── */}
      <section className="service-process" data-aos="fade-up">
        <h2 className="section-title">Selected Installations</h2>
        <p className="section-description">
          Recent specialized works by our team.
        </p>

        <div
          className="process-steps"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))",
            gap: "18px",
          }}
        >
          {/* Image 1 – plant bay */}
          <figure
            className="process-step"
            data-aos="fade-up"
            data-aos-delay="100"
            style={{ margin: 0, padding: 0 }}
          >
            <img
              src={imgBay}
              alt="Filling bay with instrumentation and shutdown devices"
              style={{ width: "100%", borderRadius: "8px" }}
            />
            <figcaption style={{ marginTop: "8px" }}>
              Filling bay: emergency shut-off, gas sensors &amp; weighing
              interface
            </figcaption>
          </figure>

          {/* Image 2 – MCC / DB */}
          <figure
            className="process-step"
            data-aos="fade-up"
            data-aos-delay="200"
            style={{ margin: 0, padding: 0 }}
          >
            <img
              src={imgMcc}
              alt="Custom MCC / distribution board"
              style={{ width: "100%", borderRadius: "8px" }}
            />
            <figcaption style={{ marginTop: "8px" }}>
              Custom MCC/DB: segregated circuits, lockable isolators &amp; clear
              labeling
            </figcaption>
          </figure>
        </div>
      </section>

      {/* ───────────────────────── PROCESS ──────────────────────────── */}
      <section className="service-process" data-aos="fade-up">
        <h2 className="section-title">Our Delivery Process</h2>
        <p className="section-description">
          From hazard study to commissioning—fully documented.
        </p>

        <div className="process-steps">
          {[
            {
              num: 1,
              title: "Hazard Study & Design",
              text:
                "Area classification, equipment selection, drawings & load schedules.",
            },
            {
              num: 2,
              title: "Panel Build & FAT",
              text:
                "MCC/DB fabrication, PLC logic and Factory Acceptance Testing.",
            },
            {
              num: 3,
              title: "Site Installation",
              text:
                "EX-rated installation, earthing/bonding & cable containment.",
            },
            {
              num: 4,
              title: "Commissioning & SAT",
              text:
                "Loop checks, functional testing, training and handover pack (CoC, test sheets).",
            },
          ].map(({ num, title, text }, i) => (
            <div
              key={i}
              className="process-step"
              data-aos="fade-up"
              data-aos-delay={100 * (i + 1)}
            >
              <div className="step-number">{num}</div>
              <h3>{title}</h3>
              <p>{text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ────────────────────────── FAQ ─────────────────────────────── 
      <section className="faq-section" data-aos="fade-up">
        <h2 className="section-title">FAQs</h2>
        <div className="faq-container">
          {[
            {
              q: "Do you work in classified zones?",
              a:
                "Yes. We design/install to SANS 10108 & IEC standards and provide apparatus selection sheets and inspection records.",
            },
            {
              q: "Can you integrate existing PLCs and SCADA?",
              a:
                "Absolutely—Siemens, Allen-Bradley, Schneider and others. We perform I/O audits, rewrite logic as needed and document changes.",
            },
            {
              q: "What documentation do we receive?",
              a:
                "Single-lines, GA layouts, panel schedules, EX registers, test sheets, FAT/SAT reports and Certificate of Compliance.",
            },
          ].map(({ q, a }, i) => (
            <div
              key={i}
              className="faq-item"
              data-aos="fade-up"
              data-aos-delay={100 * (i + 1)}
              onClick={(e) => e.currentTarget.classList.toggle("active")}
            >
              <div className="faq-question">
                <span>{q}</span>
                <i className="fas fa-chevron-down" />
              </div>
              <div className="faq-answer">
                <p>{a}</p>
              </div>
            </div>
          ))}
        </div>
      </section>
*/}
      {/* ───────────────────────── FINAL CTA ────────────────────────── */}
      <section className="cta-section" data-aos="fade-up">
        <div className="cta-content">
          <h2>Plan a Specialized Installation?</h2>
          <p>
            Let’s engineer a safe, compliant and scalable solution for your
            plant.
          </p>
        <div className="cta-buttons">
            <a href="tel:+27824989477" className="btn btn-white">
              <i className="fas fa-phone" /> Call (+27) 82 498 9477
            </a>
            <Link to="/contact" className="btn btn-outline">
              <i className="fas fa-envelope" /> Enquire Online
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
